import { useCallback } from 'react';

import { useI18n } from '@change-corgi/core-react/i18n';

import type { CreateTokenError, DLocalValidationError } from '#src/app/shared/components/payments';

import { useDLocalTranslateFieldError } from './useDLocalTranslateFieldError';

export type UseDLocalValidationErrorResult = {
	mapDLocalValidationError: (dlocalError: CreateTokenError) => {
		field: keyof DLocalValidationError;
		message: string;
	};
	translateDLocalFieldError: (field: keyof DLocalValidationError) => string | undefined;
};

const errorFieldMap: Record<string, keyof DLocalValidationError> = {
	expiration: 'expire',
	cvv: 'cvv',
	cvc: 'cvv',
	pan: 'card',
	card: 'card',
};

/**
 * @docs https://docs.dlocal.com/reference/the-fields-object
 */
export function useDLocalValidationError(): UseDLocalValidationErrorResult {
	const { translate } = useI18n();
	const translateDLocalFieldError = useDLocalTranslateFieldError();

	const mapDLocalValidationError = useCallback(
		(
			dlocalError: CreateTokenError,
		): {
			field: keyof DLocalValidationError;
			message: string;
		} => {
			const { param } = dlocalError.error || {};

			const errorField = errorFieldMap[param?.toLowerCase() ?? ''] ?? 'card';
			const message =
				translateDLocalFieldError(errorField) ?? translate('corgi.payments.dlocal.errors.incorrect_card_number');

			return {
				field: errorField,
				message,
			};
		},
		[translate, translateDLocalFieldError],
	);

	return { mapDLocalValidationError, translateDLocalFieldError };
}
